import React, { createContext, FC, useContext } from "react";
import { useTranslation } from "react-i18next";
import { ProductDefaultAttributeList } from "../../attributeHandlers";
import ProductSizeDetails from "./productSizeDetails";
import useProductDefaultStyledAttributes from "../hooks/useProductDefaultStyledAttributes";
import CustomDropdown from "@/components/customDropdown";
import ProductSizeList from "./productSizeList";
import ProductErrorInfo from "@/routes/pdp/productErrorInfo";
import useActiveSizeTypeSizes from "../hooks/useActiveSizeTypeSizes";
import useIsProductMultisize from "../hooks/useIsProductMultisize";
import useIsProductSingleSize from "../hooks/useIsProductSingleSize";
import useProductSizeTypes from "../hooks/useProductSizeTypes";
import { Product } from "@/services/productService";
import { ProductWebSizesProps } from "./types";
import { ProductErrorContext } from "@/routes/pdp/types";
import "./style.scss";

const ProductWebSizes: FC<ProductWebSizesProps> = ({
  productDetails,
  productSizeChart,
  activeSizeType,
  setActiveSizeType,
  selectedSizeFullData,
  setSelectedSizeFullData,
  showProductSizeChart,
  showShippingOptionsModal,
  withoutExtraInfo
}) => {
  const { setSelectSizeFirstError, selectSizeFirstError } =
    useContext(productErrorContext);
  const { t } = useTranslation("productDetails");
  const isProductMultisize = useIsProductMultisize(productDetails);
  const productIsSingleSize = useIsProductSingleSize(productDetails);
  const productSizeTypes = useProductSizeTypes(productDetails);
  const productDefaultStyledAttributes =
    useProductDefaultStyledAttributes(productDetails);
  const activeTypeSizes = useActiveSizeTypeSizes(
    productDetails?.sizes ?? [],
    activeSizeType
  );

  const productExtraData = (
    <div className="product_extra_data">
      <ProductDefaultAttributeList
        attributes={productDefaultStyledAttributes}
        listItemClassName="px-0 pt-3 d-flex align-items-center"
      />
    </div>
  );

  const productSizeTypesForDropdown = productSizeTypes.map(type => ({
    label: type.label,
    value: type.key
  }));

  const handleLocationDropdown = (key: string) => {
    const selectedType = productSizeTypes.find(type => type.key === key);
    if (selectedType) {
      setActiveSizeType(selectedType);
    }
  };

  return (
    <div className="pdp_web_sizes">
      {!withoutExtraInfo && (
        <ProductErrorInfo
          product={productDetails}
          selectedSizeFullData={selectedSizeFullData}
          productSizeChart={productSizeChart}
          showProductSizeChart={showProductSizeChart}
          showShippingOptionsModal={showShippingOptionsModal}
        />
      )}
      {!productIsSingleSize && activeTypeSizes && (
        <ProductSizeDetails productDetails={productDetails}>
          <ProductSizeList
            selectedSize={selectedSizeFullData}
            productSizes={activeTypeSizes}
            onSizeSelection={size => {
              setSelectSizeFirstError(false);
              setSelectedSizeFullData({ sizeType: activeSizeType, size });
            }}
          />
        </ProductSizeDetails>
      )}

      <div className="item_extra_info">
        {isProductMultisize && activeSizeType && (
          <CustomDropdown
            data={productSizeTypesForDropdown}
            selectedItem={{
              label: activeSizeType.label,
              value: activeSizeType.key
            }}
            containerClass="location_dropdown"
            scrollbarMaxHeight={150}
            handleChange={option =>
              handleLocationDropdown(option.value.toString())
            }
          />
        )}
      </div>

      {selectSizeFirstError && (
        <p className="size_selection_message disPlayNone">
          {t("sizeSelectionToProceed")}
        </p>
      )}
      {productExtraData}
    </div>
  );
};

export const productContext = createContext<Product>(null);
export const productErrorContext = createContext<ProductErrorContext>(null);

export default ProductWebSizes;
